/**
 * Hotel Details Slice
 *
 * Manages hotel details state including:
 * - Hotel details data
 * - Loading states
 * - Hotel details flags (gallery, reviews popups)
 * - Similar properties and weaver response from subsequent UUID calls
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { HotelDetailsTemplateData, SimilarProperties, WeaverResponse } from '@contracts';
import { fetchHotelDetailsThunk, fetchHotelStaticDetailsThunk } from '../thunks/detailsThunks';
import {
  setActiveTemplate,
  updateTemplateFromStream,
} from '@/features/chat/redux/slices/templateSlice';
import {
  extractIntentsFromHotelResponse,
  HOTEL_INTENTS,
} from '@/features/hotels/utils/intentHandler';
import { TEMPLATE_TYPES } from '@/features/chat/constants/templateTypes';

// ============================================
// State Types
// ============================================

/**
 * Hotel details flags for controlling popup states
 */
export interface HotelDetailsFlags {
  openGallery: boolean;
  openReviews: boolean;
}

/**
 * Payload for updating details with subsequent UUID call data
 */
export interface UpdateStaticDetailsPayload {
  similarProperties: SimilarProperties | undefined;
  weaverResponse: WeaverResponse | undefined;
  incompletedUUIDs: string[] | undefined;
}

export interface DetailsState {
  data: HotelDetailsTemplateData | null;
  isLoading: boolean;
  isLoadingStaticDetails: boolean;
  error: string | null;
  flags: HotelDetailsFlags;
}

// ============================================
// Initial State
// ============================================

export const initialDetailsState: DetailsState = {
  data: null,
  isLoading: false,
  isLoadingStaticDetails: false,
  error: null,
  flags: {
    openGallery: false,
    openReviews: false,
  },
};

// ============================================
// Slice
// ============================================

const detailsSlice = createSlice({
  name: 'hotels/details',
  initialState: initialDetailsState,
  reducers: {
    // Hotel details flags
    setHotelDetailsFlags: (state, action: PayloadAction<HotelDetailsFlags>) => {
      state.flags = action.payload;
    },

    clearHotelDetailsFlags: (state) => {
      state.flags = {
        openGallery: false,
        openReviews: false,
      };
    },

    openGallery: (state) => {
      state.flags.openGallery = true;
    },

    closeGallery: (state) => {
      state.flags.openGallery = false;
    },

    openReviews: (state) => {
      state.flags.openReviews = true;
    },

    closeReviews: (state) => {
      state.flags.openReviews = false;
    },

    // Clear hotel details
    clearHotelDetails: (state) => {
      state.data = null;
      state.isLoading = false;
      state.isLoadingStaticDetails = false;
      state.error = null;
    },

    // Reset details state
    resetDetailsState: () => initialDetailsState,

    // Update details with static details data (from subsequent UUID calls)
    updateStaticDetails: (state, action: PayloadAction<UpdateStaticDetailsPayload>) => {
      if (state.data) {
        if (action.payload.similarProperties) {
          state.data.similarProperties = action.payload.similarProperties;
        }
        if (action.payload.weaverResponse) {
          state.data.weaverResponse = action.payload.weaverResponse;
        }
        if (action.payload.incompletedUUIDs !== undefined) {
          state.data.incompletedUUIDs = action.payload.incompletedUUIDs;
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Hotel Details (initial call)
    builder
      .addCase(fetchHotelDetailsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHotelDetailsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(fetchHotelDetailsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch hotel details';
      });

    // Fetch Hotel Static Details (subsequent UUID calls)
    builder
      .addCase(fetchHotelStaticDetailsThunk.pending, (state) => {
        state.isLoadingStaticDetails = true;
      })
      .addCase(fetchHotelStaticDetailsThunk.fulfilled, (state, action) => {
        state.isLoadingStaticDetails = false;
        // Merge the static details into existing data
        if (state.data && action.payload) {
          if (action.payload.similarProperties) {
            state.data.similarProperties = action.payload.similarProperties;
          }
          if (action.payload.weaverResponse) {
            state.data.weaverResponse = action.payload.weaverResponse;
          }
          // Update incompletedUUIDs for potential further calls
          state.data.incompletedUUIDs = action.payload.incompletedUUIDs;
        }
      })
      .addCase(fetchHotelStaticDetailsThunk.rejected, (state) => {
        state.isLoadingStaticDetails = false;
      });

    /**
     * Shared handler for hotel intents embedded in template data.
     * Extracts intents (HOTEL_MEDIA, HOTEL_REVIEWS, etc.) and sets the
     * corresponding UI flags so popups open automatically.
     */
    const handleIntents = (state: DetailsState, templateData: unknown) => {
      if (!templateData) return;
      const data = templateData as Record<string, unknown>;

      const intents = extractIntentsFromHotelResponse(data);
      for (const intent of intents) {
        switch (intent) {
          case HOTEL_INTENTS.HOTEL_REVIEWS:
            state.flags.openReviews = true;
            return;
        }
      }
    };

    // History reload / card click: sync details data and open gallery or reviews
    builder.addCase(setActiveTemplate, (state, action) => {
      if (action.payload.template === TEMPLATE_TYPES.HOTEL_DETAILS && action.payload.data) {
        state.data = action.payload.data as HotelDetailsTemplateData;
      }
      handleIntents(state, action.payload.data);
    });

    // Live streaming: sync details data and open gallery or reviews
    builder.addCase(updateTemplateFromStream, (state, action) => {
      if (
        action.payload.activeTemplate === TEMPLATE_TYPES.HOTEL_DETAILS &&
        action.payload.templateData
      ) {
        state.data = action.payload.templateData as HotelDetailsTemplateData;
      }
      handleIntents(state, action.payload.templateData);
    });
  },
});

// ============================================
// Actions
// ============================================

export const {
  setHotelDetailsFlags,
  clearHotelDetailsFlags,
  openGallery,
  closeGallery,
  openReviews,
  closeReviews,
  clearHotelDetails,
  resetDetailsState,
  updateStaticDetails,
} = detailsSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const detailsReducer = detailsSlice.reducer;
